import { rd } from '@passionware/monads';
import { Users } from 'lucide-react';
import { FilterEnumConfig } from 'v5/patterns/filters/FilterWidget.types';
import { MergeServices } from 'v5/platform/ts/services';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import { WithGroupsService } from 'v5/services/io/GroupsService/GroupsService';
import { WithOptionalFilters } from 'v5/api/_common/query/queryUtils';
import { CoreFieldFilters } from 'v5/core/core-fields/core-fields-filters.types';
import { enumFilter } from 'v5/api/_common/query/filters/EnumFilter';
import { booleanFilter } from 'v5/api/_common/query/filters/BooleanFilter';

export function createGroupsFilterConfig(
  services: MergeServices<
    [
      WithMetastructureMappingService,
      WithResourceTypeService,
      WithResourceService,
      WithTagService,
      WithGroupsService
    ]
  >
): FilterEnumConfig<number, WithOptionalFilters<CoreFieldFilters>> {
  const useGroupOptions = (
    search: string,
    query: WithOptionalFilters<CoreFieldFilters>
  ) => {
    const groups = services.groupsService.useGroups({
      search: search,
      // smart way to pass core filters to groups filters, but this is co-incidental, we need resource_type_id and resource_type_model from core filters to filter groups
      filters: {
        entity_type: enumFilter.ofOneOf([17]),
        archived: booleanFilter.createDefault(false),
        ...query.filters
      }
    });
    return rd.map(groups, groups =>
      groups.map(group => ({
        value: group.id,
        label: group.name ?? '(no name)'
      }))
    );
  };

  const useGroupValues = (values: number[]) => {
    const groups = services.groupsService.useGroups({
      search: '',
      filters: {}
    });
    return rd.map(groups, groups =>
      groups
        .filter(group => values.includes(group.id))
        .map(group => ({
          value: group.id,
          label: group.name ?? '(no name)'
        }))
    );
  };

  return {
    label: 'Groups',
    icon: <Users />,
    type: 'enum',
    createDefaultValue: () => ({ operator: 'matchAny', value: [] as number[] }),
    options: {
      useValues: useGroupValues,
      useOptions: useGroupOptions,
      valueType: 'array'
    }
  };
}
